/**
 * ConfigClient - IConfig implementation initialized from API JSON response.
 * Provides the same interface as ConfigFile so loaders work uniformly with either.
 *
 * Mirrors csharp Assembler/Config/ConfigClient.cs: the API payload is held as a
 * JsonObject and every section is read through the JsonObject model (tryGetValue /
 * isObject / getObject), never flattened into plain objects or raw string-keyed maps.
 * Boundary conversion of a plain API response into JsonObject happens once, in the
 * constructor, matching the "convert at the boundary" rule.
 */

import { Logger } from '../../../Arshu/dist/logger/logger.js';
import { JsonObject } from '../app/json/jsonValue.js';
import { JsonValue } from '../app/json/jsonValue.js';
import { JsonUtility } from '../app/jsonUtility.js';
import { IConfig, AppFileResult, ArtifactKind } from './iConfig.js';
import { PreparedContent } from './preparedContent.js';

interface TemplateEntry {
    name: string;
    html: string | null;
    json: string | null;
    htmlJson: JsonObject | null;
    dataJson: JsonObject | null;
    // Opaque template-level artifacts keyed by lowercase kind: every template key other than the
    // typed Html/HtmlJson/DataJson ones (see isTypedTemplateKey). Null when none.
    artifacts: Map<string, JsonObject> | null;
}

/** Template keys this source reads as typed content (Html / HtmlJson / DataJson and their aliases). */
function isTypedTemplateKey(key: string): boolean {
    const k = key.toLowerCase();
    return k === 'html' || k === 'originalcontent' || k === 'htmljson' || k === 'datajson' || k === 'json';
}

/** AppSite keys that shape the payload itself rather than carry an artifact set. */
function isStructuralSiteKey(key: string): boolean {
    const k = key.toLowerCase();
    return k === 'appsite' || k === 'appfile' || k === 'templates' || k === 'templatesjson' || k === '_templatesjson';
}

/** An opaque artifact ships either as a JSON object or as that object's escaped JSON string. */
function parseOpaqueArtifact(value: JsonValue): JsonObject | null {
    if (value.isString) return JsonUtility.parseJsonStringStrict(value.getString());
    if (value.isObject) return value.getObject();
    return null;
}

function extractTemplateName(templateKey: string, appSiteKey: string): string {
    const prefix = `${appSiteKey}_`;
    if (templateKey.length > prefix.length && templateKey.toLowerCase().startsWith(prefix.toLowerCase())) {
        return templateKey.substring(prefix.length);
    }

    const lastUnderscore = templateKey.lastIndexOf('_');
    if (lastUnderscore >= 0 && lastUnderscore < templateKey.length - 1) {
        return templateKey.substring(lastUnderscore + 1);
    }

    return templateKey;
}

export class ConfigClient extends IConfig {
    // Configuration values from API response
    private _defaultReplaceHtml: string = '#####';
    private _defaultSkipHtml: string = '[SKIP]';
    private _defaultSkipJson: string = '$$$$$';
    private _defaultReplaceTemplateName: string = 'NotFound';
    private _defaultSkipTemplateName: string = 'Skipped';
    private _defaultAppViewPrefix: string = 'Main';
    private _maxRecursionDepth: number = 7;
    private _maxMergeIterations: number = 10;
    private _maxArrayOperationItems: number = 1;
    private _dynamicDataUseResolvedAppSite: boolean = true;
    // Delivered client-data island set (keyed "componentName_ClientDataKey" -> raw JSON), routed from the
    // SERVER merge (MergeResult.dynamicClientData). Overrides IConfig.dynamicClientData (default null on the
    // server sources); the client re-merge (clientMode) reads each component's island raw from here.
    private _dynamicClientData: JsonObject | null = null;
    private _globalDataMergeFromFallback: boolean = false;
    private _globalDataMergeOverwrite: boolean = false;
    private _replaceHandlerReturn: boolean = false;

    // Synthetic template keys (dynamicdata, dynamicview, globaldata, etc.)
    // Stored in _templates for GetDataJson access but excluded from getAllContentNames
    private _syntheticKeys: Set<string> = new Set();

    // API response data
    private _appSite: string = '';
    private _appFile: string = '';
    private _appView: string = '';
    private _searchAppSites: string = '';

    // Context JSON from API response
    private _contextJson: JsonObject = new JsonObject();

    // Templates storage - Map<appSite, Map<templateName, TemplateEntry>>
    private _templates: Map<string, Map<string, TemplateEntry>> = new Map();

    // Opaque AppSite-level artifact sets: lowercase kind -> (artifact name -> content). Every AppSite key
    // of the payload other than the structural ones (see isStructuralSiteKey) is one set, e.g. "metadata".
    private _siteArtifacts: Map<string, JsonObject> = new Map();

    // Per-AppSite root AppFile (mirrors ConfigOCI._artifactAppFile). The flat _appFile above is the
    // REQUEST AppSite's appfile only; an AppSite reached via a dynamic-AppSite redirect resolves its
    // entry template through this map. Populated from each Templates[appSite].appFile. Keyed lowercased.
    private _appFileByAppSite: Map<string, string> = new Map();

    /**
     * Creates a ConfigClient from API response data.
     * The API response is normalized to a JsonObject at this boundary so the rest of
     * the class reads it through the JsonObject model (matches csharp ConfigClient(JsonObject?)).
     */
    constructor(apiResponse: JsonObject | null = null) {
        super();
        if (apiResponse) {
            this._initFromApiResponse(apiResponse);
        }
    }

    /**
     * Initialize from API response
     */
    private _initFromApiResponse(api: JsonObject): void {
        this.loadBasicFieldsFromApi(api);
        this.loadConfigOverrides(api);
        this.loadContextJsonFromApi(api);
        this.loadTemplatesFromApi(api);

        // Store DynamicConfig/Params/GlobalData AFTER templates so they override any
        // template entries with the same name (e.g., "dynamicview").
        this._storeDynamicAndGlobalData(api);
    }

    private loadBasicFieldsFromApi(api: JsonObject): void {
        let v = api.tryGetValue('AppSite'); if (v.found && v.value.isString) this._appSite = v.value.getString();
        v = api.tryGetValue('AppFile'); if (v.found && v.value.isString) this._appFile = v.value.getString();
        v = api.tryGetValue('AppView'); if (v.found && v.value.isString) this._appView = v.value.getString();
        v = api.tryGetValue('SearchAppSites'); if (v.found && v.value.isString) this._searchAppSites = v.value.getString();
    }

    private loadConfigOverrides(api: JsonObject): void {
        const configValue = api.tryGetValue('Config');
        if (!configValue.found || !configValue.value.isObject) {
            return;
        }

        const cfg = configValue.value.getObject();
        let v = cfg.tryGetValue('defaultReplaceHtml'); if (v.found && v.value.isString) this._defaultReplaceHtml = v.value.getString();
        v = cfg.tryGetValue('defaultSkipHtml'); if (v.found && v.value.isString) this._defaultSkipHtml = v.value.getString();
        v = cfg.tryGetValue('defaultSkipJson'); if (v.found && v.value.isString) this._defaultSkipJson = v.value.getString();
        v = cfg.tryGetValue('defaultReplaceTemplateName'); if (v.found && v.value.isString) this._defaultReplaceTemplateName = v.value.getString();
        v = cfg.tryGetValue('defaultSkipTemplateName'); if (v.found && v.value.isString) this._defaultSkipTemplateName = v.value.getString();
        v = cfg.tryGetValue('defaultAppViewPrefix'); if (v.found && v.value.isString) this._defaultAppViewPrefix = v.value.getString();
        v = cfg.tryGetValue('maxRecursionDepth'); if (v.found && v.value.isInteger) this._maxRecursionDepth = v.value.getInteger();
        v = cfg.tryGetValue('maxMergeIterations'); if (v.found && v.value.isInteger) this._maxMergeIterations = v.value.getInteger();
        v = cfg.tryGetValue('maxArrayOperationItems'); if (v.found && v.value.isInteger) this._maxArrayOperationItems = v.value.getInteger();
        v = cfg.tryGetValue('dynamicDataUseResolvedAppSite'); if (v.found && v.value.isBool) this._dynamicDataUseResolvedAppSite = v.value.getBool();
        v = cfg.tryGetValue('globalDataMergeFromFallback'); if (v.found && v.value.isBool) this._globalDataMergeFromFallback = v.value.getBool();
        v = cfg.tryGetValue('globalDataMergeOverwrite'); if (v.found && v.value.isBool) this._globalDataMergeOverwrite = v.value.getBool();
        v = cfg.tryGetValue('replaceHandlerReturn'); if (v.found && v.value.isBool) this._replaceHandlerReturn = v.value.getBool();
    }

    private loadContextJsonFromApi(api: JsonObject): void {
        const contextValue = api.tryGetValue('ContextJson');
        if (contextValue.found && contextValue.value.isObject) {
            this._contextJson = contextValue.value.getObject();
        }
    }

    private loadTemplatesFromApi(api: JsonObject): void {
        const templates = this.resolveTemplatesFromAppSiteData(api);
        if (templates) {
            this._loadTemplatesFromDict(templates);
        }
    }

    private resolveTemplatesFromAppSiteData(api: JsonObject): JsonObject | null {
        // The templates section is "Templates" or an engine-prefixed "<Engine>Templates"; the payload
        // carries one, so the first present wins.
        for (const key of api.keys()) {
            if (!key.toLowerCase().endsWith('templates')) continue;
            const value = api.get(key);
            if (value.isObject) return value.getObject();
        }
        return null;
    }

    /**
     * Load templates from API response dictionary into internal storage
     * Structure: { AppSiteName: { templates: { TemplateName: { HtmlJson, DataJson } } } }
     */
    private _loadTemplatesFromDict(templatesDict: JsonObject): void {
        for (const appSiteKey of templatesDict.keys()) {
            const appSiteDataValue = templatesDict.get(appSiteKey);
            if (!appSiteDataValue.isObject) continue;
            const appSiteData = appSiteDataValue.getObject();

            // Initialize template map for this AppSite (lowercased key for O(1) lookup)
            const appKeyLower = appSiteKey.toLowerCase();
            if (!this._templates.has(appKeyLower)) {
                this._templates.set(appKeyLower, new Map());
            }
            const appSiteTemplates = this._templates.get(appKeyLower)!;

            // Per-AppSite root appfile (for dynamic-AppSite redirect targets — see _appFileByAppSite).
            const appFileVal = appSiteData.tryGetValue('appFile');
            if (appFileVal.found && appFileVal.value.isString) {
                const af = appFileVal.value.getString();
                if (af) this._appFileByAppSite.set(appKeyLower, af);
            }

            // Get templates - from various property names
            let templates: JsonObject | null = null;
            let v = appSiteData.tryGetValue('templatesJson');
            if (v.found && v.value.isString) {
                templates = JsonUtility.parseJsonString(v.value.getString());
            } else if ((v = appSiteData.tryGetValue('_templatesJson')).found && v.value.isString) {
                templates = JsonUtility.parseJsonString(v.value.getString());
            } else if ((v = appSiteData.tryGetValue('templates')).found) {
                if (v.value.isString) {
                    templates = JsonUtility.parseJsonString(v.value.getString());
                } else if (v.value.isObject) {
                    templates = v.value.getObject();
                }
            }

            if (!templates) continue;

            // Process each template
            for (const templateKey of templates.keys()) {
                const templateDataValue = templates.get(templateKey);
                if (!templateDataValue.isObject) continue;
                const templateData = templateDataValue.getObject();

                const templateEntry: TemplateEntry = {
                    name: '',
                    html: null,
                    json: null,
                    htmlJson: null,
                    dataJson: null,
                    artifacts: null
                };
                const templateName = this.extractTemplateContentFromEntry(templateKey, appSiteKey, templateData, templateEntry);
                templateEntry.name = templateName;

                appSiteTemplates.set(templateName.toLowerCase(), templateEntry);
            }

            this.storeSiteArtifactSets(appSiteData);
        }
    }

    /**
     * Stores every non-structural AppSite key as an opaque artifact set (name -> content). The kind is
     * the key without a trailing "Json" ("metadataJson" and "metadata" are the same set).
     */
    private storeSiteArtifactSets(appSiteData: JsonObject): void {
        for (const siteKey of appSiteData.keys()) {
            if (isStructuralSiteKey(siteKey)) continue;
            const set = parseOpaqueArtifact(appSiteData.get(siteKey));
            if (!set) continue;
            const keyLower = siteKey.toLowerCase();
            const kind = keyLower.endsWith('json') ? keyLower.slice(0, -4) : keyLower;
            let kindSet = this._siteArtifacts.get(kind);
            if (!kindSet) {
                kindSet = new JsonObject();
                this._siteArtifacts.set(kind, kindSet);
            }
            for (const name of set.keys()) {
                const value = set.get(name);
                if (value.isObject) kindSet.set(name, JsonValue.object(value.getObject()));
            }
        }
    }

    private extractTemplateContentFromEntry(
        templateKey: string,
        appSiteKey: string,
        templateData: JsonObject,
        templateEntry: TemplateEntry
    ): string {
        const templateName = extractTemplateName(templateKey, appSiteKey);

        let v = templateData.tryGetValue('Html');
        if (v.found && v.value.isString) {
            templateEntry.html = v.value.getString();
        } else if ((v = templateData.tryGetValue('html')).found && v.value.isString) {
            templateEntry.html = v.value.getString();
        } else if ((v = templateData.tryGetValue('originalContent')).found && v.value.isString) {
            templateEntry.html = v.value.getString();
        }

        const htmlJsonValue = templateData.tryGetValue('HtmlJson');
        if (htmlJsonValue.found) {
            if (htmlJsonValue.value.isString) {
                const htmlJsonStr = htmlJsonValue.value.getString();
                templateEntry.htmlJson = JsonUtility.parseJsonString(htmlJsonStr);
                if (!templateEntry.html) {
                    templateEntry.html = htmlJsonStr;
                }
            } else if (htmlJsonValue.value.isObject) {
                templateEntry.htmlJson = htmlJsonValue.value.getObject();
                if (!templateEntry.html) {
                    templateEntry.html = JsonUtility.serializeObject(htmlJsonValue.value.getObject());
                }
            }
        }

        const dataJsonValue = templateData.tryGetValue('DataJson');
        if (dataJsonValue.found) {
            if (dataJsonValue.value.isString) {
                const dataJsonStr = dataJsonValue.value.getString();
                templateEntry.json = dataJsonStr;
                templateEntry.dataJson = JsonUtility.parseJsonString(dataJsonStr);
            } else if (dataJsonValue.value.isObject) {
                templateEntry.dataJson = dataJsonValue.value.getObject();
                templateEntry.json = JsonUtility.serializeObject(dataJsonValue.value.getObject());
            }
        }

        if (templateEntry.dataJson === null && templateEntry.json === null) {
            const jsonLowerValue = templateData.tryGetValue('json');
            if (jsonLowerValue.found) {
                if (jsonLowerValue.value.isString) {
                    const jsonStr = jsonLowerValue.value.getString();
                    templateEntry.json = jsonStr;
                    templateEntry.dataJson = JsonUtility.parseJsonString(jsonStr);
                } else if (jsonLowerValue.value.isObject) {
                    templateEntry.dataJson = jsonLowerValue.value.getObject();
                    templateEntry.json = JsonUtility.serializeObject(jsonLowerValue.value.getObject());
                }
            }
        }

        // Every other template key is an opaque artifact of that kind, stored for getArtifactBytes/
        // getArtifactNames so the owning loader rehydrates from it. No html is derived from it.
        for (const key of templateData.keys()) {
            if (isTypedTemplateKey(key)) continue;
            const artifact = parseOpaqueArtifact(templateData.get(key));
            if (!artifact) continue;
            if (!templateEntry.artifacts) templateEntry.artifacts = new Map();
            templateEntry.artifacts.set(key.toLowerCase(), artifact);
        }

        return templateName;
    }

    // #region Loading Related - Template Access (same interface as ConfigFile)

    getAppSites(): Set<string> {
        return new Set([this._appSite]);
    }

    getAppViews(appSite: string): string[] {
        return this._appView ? [this._appView] : [];
    }

    getAppSiteFolders(): string[] {
        return [this._appSite];
    }

    getAppFile(appSite: string, appView: string): AppFileResult {
        // A dynamic-AppSite handler may redirect the merge to a DIFFERENT AppSite than the request one
        // (e.g. JsonRule83ADynAppSite -> JsonRule80ADynData). The flat _appFile is only the request
        // AppSite's appfile; for any other AppSite use its own recorded root appfile so the entry
        // template resolves (otherwise GetMainTemplate misses and the merge yields "#####").
        if (appSite && appSite.toLowerCase() !== this._appSite.toLowerCase()) {
            const resolvedAppFile = this._appFileByAppSite.get(appSite.toLowerCase());
            if (resolvedAppFile !== undefined) {
                return new AppFileResult(resolvedAppFile, appView);
            }
        }
        if (appSite && appSite.toLowerCase() !== this._appSite.toLowerCase()) {
            Logger.fallback("F-ABS3",
                `appSite absent from client per-site appfile map: appSite=${appSite} requestAppSite=${this._appSite}`);
            throw new Error(`Client payload does not contain an appfile for appSite '${appSite}'.`);
        }
        // Same AppSite: the appfile is the root _appFile regardless of appView (mirrors ConfigFile,
        // which derives the appfile from the root HTML, not the appView). But a non-empty appView that
        // differs from the request appView is a dynamic-AppSite appView redirect (e.g. Admin -> Login):
        // honor it as the validated appView so the correct view renders instead of reverting to the
        // request view. Empty/equal appView keeps the request's stored _appView.
        const validatedAppView = (appView && appView.toLowerCase() !== this._appView.toLowerCase())
            ? appView
            : this._appView;
        if (appView && appView.toLowerCase() !== this._appView.toLowerCase()) {
            const validationTemplates = this._templates.get((appSite || this._appSite).toLowerCase());
            if (!validationTemplates || !validationTemplates.has(appView.toLowerCase())) {
                Logger.fallback("F-ABS2",
                    `appView absent from client payload: appSite=${appSite} appView=${appView} storedAppView=${this._appView}`);
                throw new Error(`Client payload does not contain appView '${appView}' for appSite '${appSite}'.`);
            }
        }
        return new AppFileResult(this._appFile, validatedAppView);
    }

    /**
     * Gets all template names for the specified AppSite
     * Same interface as ConfigFile - loader uses this to enumerate templates
     */
    getAllContentNames(appSite: string): string[] {
        const appTemplates = this._templates.get(appSite.toLowerCase());
        if (!appTemplates) {
            return [];
        }
        const result: string[] = [];
        for (const [key, entry] of appTemplates) {
            if (!this._syntheticKeys.has(key)) {
                result.push(entry.name);
            }
        }
        return result;
    }

    /**
     * Gets HTML content for a template
     * Same interface as ConfigFile - loader uses this to get template HTML
     * For ConfigClient, we return the raw HTML string (or HtmlJson as string)
     */
    getHtmlContent(appSite: string, fileName: string): string | null {
        const appSiteTemplates = this._templates.get(appSite.toLowerCase());
        if (appSiteTemplates) {
            const entry = appSiteTemplates.get(fileName.toLowerCase());
            if (entry) return entry.html;
        }
        Logger.fallback("F-ABS3",
            `appSite/fileName absent from client payload: appSite=${appSite} fileName=${fileName}`);
        throw new Error(`Client payload does not contain template '${fileName}' for appSite '${appSite}'.`);
    }

    /**
     * Gets parsed DataJson structure for a template (optimization for JSON engines)
     * ConfigClient stores pre-parsed structures from API response
     */
    getDataJson(appSite: string, fileName: string): JsonObject | null {
        const appSiteTemplates = this._templates.get(appSite.toLowerCase());
        if (!appSiteTemplates) return null;
        const entry = appSiteTemplates.get(fileName.toLowerCase());
        if (!entry) return null;
        return entry.dataJson;
    }

    // #region Source Artifact Access (format-blind)

    /** Client source: serves pre-parsed payload content, so an absent artifact is a defect. */
    override get isArtifactSource(): boolean {
        return true;
    }

    /** Client source: the API payload is already in memory, so content reads are answered by this source (resident mode). */
    override prepare(appSites: string[]): PreparedContent {
        void appSites;
        return PreparedContent.resident(this);
    }

    getArtifactBytes(appSite: string, name: string, kind: string): Uint8Array | null {
        switch (kind) {
            case ArtifactKind.HtmlJson: {
                const siteTemplates = this._templates.get(appSite.toLowerCase());
                if (!siteTemplates) return null;
                const entry = siteTemplates.get(name.toLowerCase());
                if (!entry || !entry.htmlJson) return null;
                return this._serializeToUtf8(entry.htmlJson);
            }
            default: {
                // Opaque artifacts: a template-level one of this kind, else a member of the AppSite-level
                // set of this kind. The source never interprets them; the owning loader decodes the bytes.
                const kindLower = kind.toLowerCase();
                const entry = this._templates.get(appSite.toLowerCase())?.get(name.toLowerCase());
                const artifact = entry?.artifacts?.get(kindLower);
                if (artifact) return this._serializeToUtf8(artifact);
                const member = this._siteArtifacts.get(kindLower)?.tryGetValue(name);
                if (member && member.found && member.value.isObject) return this._serializeToUtf8(member.value.getObject());
                return null;
            }
        }
    }

    /**
     * Enumerates the artifact names of an opaque kind. An AppSite-level set names its members
     * "{appSite}" and "{appSite}_{appView}"; otherwise the names are the AppSite's templates carrying a
     * template-level artifact of the kind. Template enumeration stays on getAllContentNames.
     */
    getArtifactNames(appSite: string, kind: string): string[] {
        const kindLower = kind.toLowerCase();
        const kindSet = this._siteArtifacts.get(kindLower);
        if (kindSet) {
            const appSiteLower = appSite.toLowerCase();
            const prefix = appSiteLower + '_';
            const keys: string[] = [];
            for (const key of kindSet.keys()) {
                const keyLower = key.toLowerCase();
                if (keyLower === appSiteLower || keyLower.startsWith(prefix)) keys.push(key);
            }
            return keys;
        }
        const siteTemplates = this._templates.get(appSite.toLowerCase());
        if (siteTemplates) {
            const keys: string[] = [];
            for (const [key, entry] of siteTemplates) {
                if (entry.artifacts && entry.artifacts.has(kindLower)) keys.push(key);
            }
            return keys;
        }
        return [];
    }

    private _serializeToUtf8(obj: JsonObject): Uint8Array {
        return new TextEncoder().encode(JsonUtility.serializeObject(obj));
    }

    // #endregion

    getRelativeInfo(appSite: string, fileName: string): string {
        return '';
    }

    getAppSiteFolder(appSite: string): string | null {
        return null;
    }

    // #endregion

    // #region Engine Loading Configuration

    getSearchAppSitesForAppSite(appSite: string): string {
        return this._searchAppSites;
    }

    get defaultAppViewPrefix(): string {
        return this._defaultAppViewPrefix;
    }

    get defaultReplaceHtml(): string {
        return this._defaultReplaceHtml;
    }

    get defaultReplaceTemplateName(): string {
        return this._defaultReplaceTemplateName;
    }

    get defaultSkipHtml(): string {
        return this._defaultSkipHtml;
    }

    get defaultSkipJson(): string {
        return this._defaultSkipJson;
    }

    get defaultSkipTemplateName(): string {
        return this._defaultSkipTemplateName;
    }

    // #endregion

    // #region Engine Configuration

    get maxArrayOperationItems(): number {
        return this._maxArrayOperationItems;
    }

    get maxRecursionDepth(): number {
        return this._maxRecursionDepth;
    }

    get maxMergeIterations(): number {
        return this._maxMergeIterations;
    }

    get dynamicDataUseResolvedAppSite(): boolean {
        return this._dynamicDataUseResolvedAppSite;
    }

    override get dynamicClientData(): JsonObject | null {
        return this._dynamicClientData;
    }

    set dynamicClientData(value: JsonObject | null) {
        this._dynamicClientData = value;
    }

    get globalDataMergeFromFallback(): boolean {
        return this._globalDataMergeFromFallback;
    }

    get globalDataMergeOverwrite(): boolean {
        return this._globalDataMergeOverwrite;
    }

    get replaceHandlerReturn(): boolean {
        return this._replaceHandlerReturn;
    }

    getRootJson(fileName: string): JsonObject {
        // Return context JSON for Context.json
        if (fileName.toLowerCase() === 'context.json') {
            return this._contextJson;
        }
        return new JsonObject();
    }

    // #endregion

    // #region Folder-Level Data Support

    /**
     * Client mode: no folder-level data (loader handles via client-mode fields)
     */
    getFolderDataJson(appSite: string, fileName: string): JsonObject | null {
        return null;
    }

    // #endregion

    // #region Store Dynamic and Global Data

    /**
     * Stores dynamic and global data from API response as synthetic template entries.
     * Called AFTER template loading in initFromApiResponse.
     * Loader's PreloadDynamicAndGlobalData will pick these up via getDataJson().
     */
    private _storeDynamicAndGlobalData(api: JsonObject): void {
        if (this._shouldUsePerSiteStores(api)) {
            this._storePerSiteData(api.tryGetValue('PerSiteStores').value.getObject());
            return;
        }

        this.storeFlatSyntheticTemplates(api);
    }

    private _shouldUsePerSiteStores(api: JsonObject): boolean {
        const perSiteValue = api.tryGetValue('PerSiteStores');
        return perSiteValue.found && perSiteValue.value.isObject;
    }

    private storeFlatSyntheticTemplates(api: JsonObject): void {
        const appSiteLower = this._appSite.toLowerCase();
        if (!this._templates.has(appSiteLower)) {
            this._templates.set(appSiteLower, new Map());
        }
        const appSiteTemplates = this._templates.get(appSiteLower)!;

        // Flat (non-per-site) payload: reconstruct the same client-executed tiers as _storePerSiteData.
        this._storeFlatDynamicConfig(api, appSiteTemplates, 'DynamicViewConfig', 'dynamicview', 'DynamicViewParams', /DynamicView\./i);
        this._storeFlatDynamicConfig(api, appSiteTemplates, 'DynamicClientConfig', 'dynamicclientdata', 'DynamicClientParams', /DynamicClientData\./i);
        this.storePerSiteGlobalData(api, appSiteTemplates);
        this.storePerSiteGlobalViewData(api, appSiteTemplates);
    }

    private _storeFlatDynamicConfig(
        api: JsonObject,
        siteTemplates: Map<string, TemplateEntry>,
        configKey: string,
        syntheticName: string,
        paramsKey: string,
        paramsRegex: RegExp
    ): void {
        const configValue = api.tryGetValue(configKey);
        if (configValue.found && configValue.value.isObject) {
            siteTemplates.set(syntheticName, { name: syntheticName, html: null, json: null, htmlJson: null, dataJson: configValue.value.getObject(), artifacts: null });
            this._syntheticKeys.add(syntheticName);
        }
        const paramsValue = api.tryGetValue(paramsKey);
        if (paramsValue.found && paramsValue.value.isObject) {
            const params = paramsValue.value.getObject();
            for (const paramKey of params.keys()) {
                const pv = params.get(paramKey);
                if (pv.isObject) {
                    const fileName = paramKey.replace(paramsRegex, `${syntheticName}.`);
                    siteTemplates.set(fileName.toLowerCase(), { name: fileName, html: null, json: null, htmlJson: null, dataJson: pv.getObject(), artifacts: null });
                    this._syntheticKeys.add(fileName.toLowerCase());
                }
            }
        }
    }

    private _storeGlobalViewDataEntries(siteTemplates: Map<string, TemplateEntry>, gvd: JsonObject): void {
        siteTemplates.set('globalviewdata', { name: 'globalviewdata', html: null, json: null, htmlJson: null, dataJson: gvd, artifacts: null });
        this._syntheticKeys.add('globalviewdata');
        for (const componentName of gvd.keys()) {
            const componentValue = gvd.get(componentName);
            if (componentValue.isObject) {
                const syntheticKey = `globalviewdata.${componentName}`.toLowerCase();
                siteTemplates.set(syntheticKey, { name: `globalviewdata.${componentName}`, html: null, json: null, htmlJson: null, dataJson: componentValue.getObject(), artifacts: null });
                this._syntheticKeys.add(syntheticKey);
            }
        }
    }

    private _ensureSiteTemplates(site: string): Map<string, TemplateEntry> {
        const siteLower = site.toLowerCase();
        if (!this._templates.has(siteLower)) {
            this._templates.set(siteLower, new Map());
        }
        return this._templates.get(siteLower)!;
    }

    private _storePerSiteData(perSiteStores: JsonObject): void {
        this.storePerSiteGlobalData(perSiteStores);
        this.storePerSiteGlobalViewData(perSiteStores);

        // Mirrors the loaders' serializePerSiteStores emitter: api.json carries the config of the tiers that
        // EXECUTE on the client. Server-only Data/Action/AppSite handlers — and the DynamicClientData
        // PREPARER — are intentionally NOT shipped: a browser cannot run the server preparer, so the client
        // sources the client-data RAW from the DELIVERED island, never by re-running the preparer.
        this.storePerSiteDynamicConfig(perSiteStores, 'DynamicViewConfig', 'dynamicview');
        this.storePerSiteDynamicParams(perSiteStores, 'DynamicViewParams', 'dynamicview');

        // PURE client bindings ARE shipped — the pure handler itself runs on the client and resolves its params
        // from the binding, so withholding these would make the re-merge silently skip it. Stored under the
        // same synthetic names the loader preload reads (dynamicclientdata / dynamicclientdata.<ParamFile>),
        // so no loader-side special case is needed. Only Pure bindings are in the payload; the Embedded
        // preparer's binding and params never leave the server.
        this.storePerSiteDynamicConfig(perSiteStores, 'DynamicClientConfig', 'dynamicclientdata');
        this.storePerSiteDynamicParams(perSiteStores, 'DynamicClientParams', 'dynamicclientdata');
    }

    private storePerSiteGlobalData(api: JsonObject, siteTemplates: Map<string, TemplateEntry>): void;
    private storePerSiteGlobalData(perSiteStores: JsonObject): void;
    private storePerSiteGlobalData(api: JsonObject, siteTemplates?: Map<string, TemplateEntry>): void {
        if (siteTemplates !== undefined) {
            const gd = api.tryGetValue('GlobalData');
            if (gd.found && gd.value.isObject) {
                siteTemplates.set('globaldata', { name: 'globaldata', html: null, json: null, htmlJson: null, dataJson: gd.value.getObject(), artifacts: null });
                this._syntheticKeys.add('globaldata');
            }
            return;
        }

        const gdValue = api.tryGetValue('GlobalData');
        if (!gdValue.found || !gdValue.value.isObject) {
            return;
        }

        this._syntheticKeys.add('globaldata');
        const globalData = gdValue.value.getObject();
        for (const siteKey of globalData.keys()) {
            const currentSiteTemplates = this._ensureSiteTemplates(siteKey);
            const siteValue = globalData.get(siteKey);
            currentSiteTemplates.set('globaldata', { name: 'globaldata', html: null, json: null, htmlJson: null, dataJson: siteValue.isObject ? siteValue.getObject() : null, artifacts: null });
        }
    }

    private storePerSiteGlobalViewData(api: JsonObject, siteTemplates: Map<string, TemplateEntry>): void;
    private storePerSiteGlobalViewData(perSiteStores: JsonObject): void;
    private storePerSiteGlobalViewData(api: JsonObject, siteTemplates?: Map<string, TemplateEntry>): void {
        if (siteTemplates !== undefined) {
            const gvd = api.tryGetValue('GlobalViewData');
            if (gvd.found && gvd.value.isObject) {
                this._storeGlobalViewDataEntries(siteTemplates, gvd.value.getObject());
            }
            return;
        }

        const gvdValue = api.tryGetValue('GlobalViewData');
        if (!gvdValue.found || !gvdValue.value.isObject) {
            return;
        }

        this._syntheticKeys.add('globalviewdata');
        const globalViewData = gvdValue.value.getObject();
        for (const siteKey of globalViewData.keys()) {
            const currentSiteTemplates = this._ensureSiteTemplates(siteKey);
            const siteValue = globalViewData.get(siteKey);
            if (siteValue.isObject) {
                this._storeGlobalViewDataEntries(currentSiteTemplates, siteValue.getObject());
            } else {
                currentSiteTemplates.set('globalviewdata', { name: 'globalviewdata', html: null, json: null, htmlJson: null, dataJson: null, artifacts: null });
            }
        }
    }

    private storePerSiteDynamicConfig(perSiteStores: JsonObject, storeKey: string, syntheticName: string): void {
        const storeValue = perSiteStores.tryGetValue(storeKey);
        if (!storeValue.found || !storeValue.value.isObject) return;
        const store = storeValue.value.getObject();
        this._syntheticKeys.add(syntheticName);
        for (const siteKey of store.keys()) {
            const siteTemplates = this._ensureSiteTemplates(siteKey);
            const siteValue = store.get(siteKey);
            siteTemplates.set(syntheticName, { name: syntheticName, html: null, json: null, htmlJson: null, dataJson: siteValue.isObject ? siteValue.getObject() : null, artifacts: null });
        }
    }

    private storePerSiteDynamicParams(perSiteStores: JsonObject, storeKey: string, syntheticPrefix: string): void {
        const storeValue = perSiteStores.tryGetValue(storeKey);
        if (!storeValue.found || !storeValue.value.isObject) return;
        const store = storeValue.value.getObject();
        for (const siteKey of store.keys()) {
            const siteValue = store.get(siteKey);
            if (!siteValue.isObject) continue;
            const siteParams = siteValue.getObject();
            const siteTemplates = this._ensureSiteTemplates(siteKey);
            for (const paramKey of siteParams.keys()) {
                const paramValue = siteParams.get(paramKey);
                if (paramValue.isObject) {
                    // paramKey is like "DynamicData.AppSiteFoldersData" -> store as "dynamicdata.AppSiteFoldersData"
                    const upperPrefix = syntheticPrefix.charAt(0).toUpperCase() + syntheticPrefix.substring(1);
                    const regex = new RegExp(`^${upperPrefix}\\.`, 'i');
                    const fileName = paramKey.replace(regex, `${syntheticPrefix}.`);
                    siteTemplates.set(fileName.toLowerCase(), { name: fileName, html: null, json: null, htmlJson: null, dataJson: paramValue.getObject(), artifacts: null });
                    this._syntheticKeys.add(fileName.toLowerCase());
                }
            }
        }
    }

    // #endregion
}
